import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BarChart3, ArrowLeft, Users, Building2,
  UserCheck, FileText, Brain, Key, Activity, Zap, Volume2
} from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { isUserAdmin } from '../../lib/auth';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { AdminAssessmentsChart } from '../../components/admin/AdminAssessmentsChart';
import { AdminCodeRedemption } from '../../components/admin/AdminCodeRedemption';
import { CoachTierChart } from '../../components/admin/CoachTierChart';
import { GrowthChart } from '../../components/admin/GrowthChart';
import { PartnerOverviewTable } from '../../components/admin/PartnerOverviewTable';
import {
  fetchAdminStats,
  fetchAdminCodeRedemption,
  fetchAssessmentsOverTimeAdmin,
  fetchCoachTierBreakdown,
  fetchMonthlyGrowth,
  fetchPartnerOverviews,
  type AdminStats,
  type AdminCodeRedemption as AdminCodeRedemptionData,
  type MonthlyAssessmentAdmin,
  type CoachTierBreakdown,
  type MonthlyGrowth,
  type PartnerOverview,
} from '../../lib/admin-dashboard';

export function Analytics() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<AdminStats>({
    totalPartners: 0,
    totalCoaches: 0,
    totalRespondents: 0,
    totalAssessments: 0,
    totalCodes: 0,
    codesUsed: 0,
    codesAvailable: 0,
    aiAnalysisCount: 0,
  });
  const [codeRedemption, setCodeRedemption] = useState<AdminCodeRedemptionData>({ totalCodes: 0, usedCodes: 0, rate: 0 });
  const [assessmentsOverTime, setAssessmentsOverTime] = useState<MonthlyAssessmentAdmin[]>([]);
  const [coachTiers, setCoachTiers] = useState<CoachTierBreakdown[]>([]);
  const [growth, setGrowth] = useState<MonthlyGrowth[]>([]);
  const [partnerOverviews, setPartnerOverviews] = useState<PartnerOverview[]>([]);
  const [platformAiUsage, setPlatformAiUsage] = useState({ totalTokens: 0, totalVoiceMinutes: 0, topCoaches: [] as { name: string; tokens: number; voiceMinutes: number }[] });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }

    const init = async () => {
      try {
        const isAdmin = await isUserAdmin(user.id);
        if (!isAdmin) {
          navigate('/login');
          return;
        }
        await loadAnalytics();
      } catch (err) {
        console.error('Error checking admin status:', err);
        navigate('/login');
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const loadAnalytics = async () => {
    try {
      const [
        statsData,
        codeData,
        assessmentsData,
        tiersData,
        growthData,
        partnersData,
      ] = await Promise.all([
        fetchAdminStats(),
        fetchAdminCodeRedemption(),
        fetchAssessmentsOverTimeAdmin(),
        fetchCoachTierBreakdown(),
        fetchMonthlyGrowth(),
        fetchPartnerOverviews(),
      ]);

      setStats(statsData);
      setCodeRedemption(codeData);
      setAssessmentsOverTime(assessmentsData);
      setCoachTiers(tiersData);
      setGrowth(growthData);
      setPartnerOverviews(partnersData);

      // Platform-wide AI usage this month
      const monthStart = new Date();
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);

      const { data: aiLogs } = await (supabase
        .from('ai_coach_usage_logs')
        .select('coach_id, type, duration_seconds, token_input, token_output')
        .gte('created_at', monthStart.toISOString()) as unknown as Promise<{ data: Array<{ coach_id: string; type: string; duration_seconds: number | null; token_input: number | null; token_output: number | null }> | null; error: unknown }>);

      const coachTokenMap = new Map<string, { tokens: number; voiceSeconds: number }>();
      let totalTokens = 0;
      let totalVoiceSeconds = 0;
      (aiLogs || []).forEach(log => {
        const entry = coachTokenMap.get(log.coach_id) || { tokens: 0, voiceSeconds: 0 };
        if (log.type === 'chat') {
          const t = (log.token_input || 0) + (log.token_output || 0);
          entry.tokens += t;
          totalTokens += t;
        } else {
          entry.voiceSeconds += log.duration_seconds || 0;
          totalVoiceSeconds += log.duration_seconds || 0;
        }
        coachTokenMap.set(log.coach_id, entry);
      });

      const topCoachIds = [...coachTokenMap.entries()]
        .sort((a, b) => b[1].tokens - a[1].tokens)
        .slice(0, 10)
        .map(([id]) => id);

      let topCoaches: { name: string; tokens: number; voiceMinutes: number }[] = [];
      if (topCoachIds.length > 0) {
        const { data: coachUsers } = await supabase
          .from('coaches')
          .select('id, users!coaches_user_id_fkey(full_name, email)')
          .in('id', topCoachIds);
        topCoaches = topCoachIds.map(id => {
          const c = (coachUsers || []).find(x => x.id === id);
          const u = coachTokenMap.get(id)!;
          return {
            name: (c?.users as any)?.full_name || (c?.users as any)?.email || id.slice(0, 8),
            tokens: u.tokens,
            voiceMinutes: Math.round(u.voiceSeconds / 60),
          };
        });
      }

      setPlatformAiUsage({ totalTokens, totalVoiceMinutes: Math.round(totalVoiceSeconds / 60), topCoaches });
    } catch (err) {
      console.error('Error loading analytics:', err);
    }
  };

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Loading analytics..." />;
  }

  const aiRate = stats.totalAssessments > 0
    ? Math.round((stats.aiAnalysisCount / stats.totalAssessments) * 100)
    : 0;

  const statCards = [
    { label: 'Partners', value: stats.totalPartners, icon: Building2, bgColor: 'bg-brand-10', iconColor: 'text-brand-40' },
    { label: 'Coaches', value: stats.totalCoaches, icon: UserCheck, bgColor: 'bg-success-10', iconColor: 'text-success-40' },
    { label: 'Respondents', value: stats.totalRespondents, icon: Users, bgColor: 'bg-orange-10', iconColor: 'text-orange-40' },
    { label: 'Assessments', value: stats.totalAssessments, icon: FileText, bgColor: 'bg-teal-10', iconColor: 'text-teal-50' },
    { label: 'Codes Available', value: stats.codesAvailable, icon: Key, bgColor: 'bg-brand-10', iconColor: 'text-brand-60' },
    { label: 'AI Analyses', value: stats.aiAnalysisCount, icon: Brain, bgColor: 'bg-success-5', iconColor: 'text-success-50' },
  ];

  return (
    <div className="min-h-screen bg-brand-5">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Link
            to="/admin"
            className="flex items-center gap-2 text-gray-50 hover:text-success-50 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-success-10 rounded-xl">
              <BarChart3 className="w-6 h-6 text-success-40" />
            </div>
            <h1 className="text-heading-xs text-gray-80">Platform Analytics</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.06 }}
              className="freud-card-elevated p-5"
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`p-2.5 ${stat.bgColor} rounded-xl`}>
                  <stat.icon className={`w-5 h-5 ${stat.iconColor}`} />
                </div>
              </div>
              <div className="text-heading-sm text-gray-80 mb-0.5">{stat.value}</div>
              <div className="text-text-xs text-gray-40">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          <GrowthChart data={growth} />
          <AdminAssessmentsChart data={assessmentsOverTime} />
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          <AdminCodeRedemption data={codeRedemption} />
          <CoachTierChart tiers={coachTiers} totalCoaches={stats.totalCoaches} />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="freud-card-elevated p-6 mb-8"
        >
          <div className="flex items-center gap-2 mb-5">
            <Brain className="w-5 h-5 text-success-40" />
            <h3 className="text-text-xl text-gray-80">AI Analysis Usage</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-4 bg-success-5 rounded-xl">
              <div className="text-heading-sm text-gray-80">{stats.aiAnalysisCount}</div>
              <div className="text-text-xs text-gray-40">Analyses Generated</div>
            </div>
            <div className="p-4 bg-brand-10 rounded-xl">
              <div className="text-heading-sm text-gray-80">{stats.totalAssessments - stats.aiAnalysisCount}</div>
              <div className="text-text-xs text-gray-40">Without AI Analysis</div>
            </div>
            <div className="p-4 bg-teal-10 rounded-xl">
              <div className="text-heading-sm text-gray-80">{aiRate}%</div>
              <div className="text-text-xs text-gray-40">AI Adoption Rate</div>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex justify-between text-text-xs mb-2">
              <span className="text-gray-50">AI Analysis Coverage</span>
              <span className="text-gray-80 font-semibold">{aiRate}%</span>
            </div>
            <div className="w-full bg-brand-10 rounded-full h-2">
              <motion.div
                className="bg-success-40 h-2 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${aiRate}%` }}
                transition={{ duration: 0.8 }}
              />
            </div>
          </div>
        </motion.div>

        <div className="mb-8">
          <PartnerOverviewTable partners={partnerOverviews} />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="freud-card-elevated p-6"
        >
          <div className="flex items-center gap-2 mb-5">
            <Activity className="w-5 h-5 text-brand-40" />
            <h3 className="text-text-xl text-gray-80">Capacity Utilization</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="flex justify-between text-text-sm mb-2">
                <span className="text-gray-50">Coach Capacity (across all partners)</span>
                <span className="text-gray-80 font-semibold">
                  {partnerOverviews.reduce((sum, p) => sum + p.coachCount, 0)} / {partnerOverviews.reduce((sum, p) => sum + p.maxCoaches, 0)}
                </span>
              </div>
              <div className="w-full bg-brand-10 rounded-full h-3">
                <motion.div
                  className="bg-success-40 h-3 rounded-full"
                  initial={{ width: 0 }}
                  animate={{
                    width: (() => {
                      const total = partnerOverviews.reduce((s, p) => s + p.maxCoaches, 0);
                      const used = partnerOverviews.reduce((s, p) => s + p.coachCount, 0);
                      return total > 0 ? `${Math.round((used / total) * 100)}%` : '0%';
                    })()
                  }}
                  transition={{ duration: 0.8 }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-text-sm mb-2">
                <span className="text-gray-50">Code Utilization</span>
                <span className="text-gray-80 font-semibold">
                  {stats.codesUsed} / {stats.totalCodes}
                </span>
              </div>
              <div className="w-full bg-brand-10 rounded-full h-3">
                <motion.div
                  className="bg-brand-40 h-3 rounded-full"
                  initial={{ width: 0 }}
                  animate={{
                    width: stats.totalCodes > 0 ? `${Math.round((stats.codesUsed / stats.totalCodes) * 100)}%` : '0%'
                  }}
                  transition={{ duration: 0.8 }}
                />
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="freud-card-elevated p-6 mt-8"
        >
          <div className="flex items-center gap-2 mb-5">
            <Zap className="w-5 h-5 text-teal-500" />
            <h3 className="text-text-xl text-gray-80">AI Coach Usage This Month (Platform-wide)</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="p-4 bg-teal-10 rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-4 h-4 text-teal-500" />
                <span className="text-text-xs text-gray-40">Total Tokens</span>
              </div>
              <div className="text-heading-sm text-gray-80">{platformAiUsage.totalTokens.toLocaleString()}</div>
            </div>
            <div className="p-4 bg-orange-10 rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <Volume2 className="w-4 h-4 text-orange-40" />
                <span className="text-text-xs text-gray-40">Total Voice Minutes</span>
              </div>
              <div className="text-heading-sm text-gray-80">{platformAiUsage.totalVoiceMinutes.toLocaleString()}</div>
            </div>
          </div>
          {platformAiUsage.topCoaches.length > 0 && (
            <>
              <h4 className="text-text-sm font-semibold text-gray-60 mb-3">Top Coaches by Token Usage</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-20">
                      <th className="text-left py-2 px-3 font-semibold text-gray-50">Coach</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-50">Tokens</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-50">Voice Min</th>
                    </tr>
                  </thead>
                  <tbody>
                    {platformAiUsage.topCoaches.map((c, i) => (
                      <tr key={i} className="border-b border-gray-10 hover:bg-brand-5">
                        <td className="py-2 px-3 font-medium text-gray-80">{c.name}</td>
                        <td className="py-2 px-3 text-right text-gray-80">{c.tokens.toLocaleString()}</td>
                        <td className="py-2 px-3 text-right text-gray-80">{c.voiceMinutes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
}
